import { ReactElement } from 'react';
import { Trans, useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { ComparisonText } from './comparison-text';
import { nsrTimePeriods } from './filters';

interface ChangeProps {
  forTooltip?: boolean;
  formerMetric?: string;
  formerValue?: number;
  latterMetric?: string;
  latterValue?: number;
  timePeriod?: keyof typeof nsrTimePeriods;
}

export const NSRTypeComparisonText = ({
  forTooltip,
  formerMetric,
  formerValue,
  latterMetric,
  latterValue,
  timePeriod,
}: ChangeProps) => {
  const { t } = useTranslation('analytics');
  const difference = Math.round(formerValue ?? 0) - Math.round(latterValue ?? 0);

  let content: ReactElement | string = <></>;

  let currentPeriod = '';

  switch (timePeriod) {
    case 'lastMonth':
      currentPeriod = t('last month');
      break;

    case 'thisMonth':
      currentPeriod = t('this month');
      break;

    case 'thisQuarter':
      currentPeriod = t('this quarter');
      break;

    default:
      currentPeriod = '';
      break;
  }

  const transValues = {
    difference: formatters.appendPercent(Math.abs(difference)),
  };

  const lowerCaseLatterMetric = latterMetric?.toLocaleLowerCase();

  if (forTooltip) {
    if (difference > 0) {
      content = (
        <Trans t={t} values={transValues}>
          {{ formerMetric }} show{' '}
          <strong style={{ color: theme.colors.critical50 }}>{{ difference: transValues.difference }}</strong> higher
          no-show rate compared to {{ lowerCaseLatterMetric }}
        </Trans>
      );
    } else if (difference < 0) {
      content = (
        <Trans t={t} values={transValues}>
          {{ formerMetric }} show <strong>{{ difference: transValues.difference }}</strong> lower no-show rate compared
          to {{ lowerCaseLatterMetric }}
        </Trans>
      );
    } else {
      content = t('{{ formerMetric }} show the same no-show rate as {{ lowerCaseLatterMetric }}', {
        formerMetric,
        lowerCaseLatterMetric,
      });
    }
    return content;
  }

  if (timePeriod === 'thisMonth' || timePeriod === 'thisQuarter') {
    if (difference > 0) {
      content = (
        <Trans t={t} values={transValues}>
          {{ formerMetric }} have an average no-show rate <strong>{{ difference: transValues.difference }}</strong>{' '}
          higher than {{ lowerCaseLatterMetric }} {{ currentPeriod }}
        </Trans>
      );
    } else if (difference < 0) {
      content = (
        <Trans t={t} values={transValues}>
          {{ formerMetric }} have an average no-show rate <strong>{{ difference: transValues.difference }}</strong>{' '}
          lower than {{ lowerCaseLatterMetric }} {{ currentPeriod }}
        </Trans>
      );
    } else {
      content = t('{{ formerMetric }} have the same no-show rate as {{ lowerCaseLatterMetric }} {{ currentPeriod }}', {
        currentPeriod,
        formerMetric,
        lowerCaseLatterMetric,
      });
    }
  } else {
    if (difference > 0) {
      content = (
        <Trans t={t} values={transValues}>
          {{ formerMetric }} had an average no-show rate <strong>{{ difference: transValues.difference }}</strong>{' '}
          higher than {{ lowerCaseLatterMetric }} {{ currentPeriod }}
        </Trans>
      );
    } else if (difference < 0) {
      content = (
        <Trans t={t} values={transValues}>
          {{ formerMetric }} had an average no-show rate <strong>{{ difference: transValues.difference }}</strong> lower
          than {{ lowerCaseLatterMetric }} {{ currentPeriod }}
        </Trans>
      );
    } else {
      content = t('{{ formerMetric }} had the same no-show rate as {{ lowerCaseLatterMetric }} {{ currentPeriod }}', {
        currentPeriod,
        formerMetric,
        lowerCaseLatterMetric,
      });
    }
  }

  return <ComparisonText content={content} />;
};
